import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import api from 'common/api'
import useIsShadow from 'common/use-is-shadow'
import { useFetchRequirements } from 'routes/application/AppCycleRounds/RoundRequirementsProvider.tsx'
import { useAppCycle } from 'store/application-cycle/hooks'
import { useAppRoundEditDisabled, useConfigureUpcomingApplicationCycle } from 'store/feature-toggle/hooks'
import { useCanManageRoundsAndRequirements, useScid } from 'store/user/hooks'

import { AppRound } from './types'

export const dateRelativeToUTC = (input: Date | string) => {
  const cutoffUTCDateTime = new Date(input)
  const date = cutoffUTCDateTime.getUTCDate()
  const month = cutoffUTCDateTime.getUTCMonth()
  const year = cutoffUTCDateTime.getUTCFullYear()
  return new Date(Date.UTC(year, month, date, 23, 59, 59, 999))
}

type contextValueType = {
  rounds?: AppRound[]
  applicationCycle: number
  total?: number
  updateRound: (round: AppRound) => Promise<unknown>
  deleteRound: (roundId: string) => Promise<unknown>
  copyRounds: (appCycle: number) => Promise<void>
}

const Context = React.createContext({} as contextValueType)

type ApiRoundDate = {
  Day: number
  Month: number
}
type ApiRound = {
  Id: string
  UpdatedBy: string
  UpdatedAt: string
  UpdatedByName: string
  UpdatedBySystem: string
  Scid: number
  Name: string
  Binding: boolean
  ApplicantType?: 'firstYear' | 'transfer' | undefined
  CustomType?: string
  RoundCutOff?: ApiRoundDate // To Be Cutoff?
  CutoffDate?: string
  DueDate?: ApiRoundDate // To Be Deadline?
  DeadlineDate?: string
  MaterialsDeadlineDate?: string
  RequirementsId?: string
}
type RoundApi = ApiRound[]

export const useFocusedApplicationCycle = () => {
  const { applicationCycle } = useContext(Context)
  return applicationCycle
}

export const useRounds = (): AppRound[] => {
  const { rounds } = useContext(Context)
  return rounds ?? []
}
export const useUpdateRound = () => {
  const { updateRound } = useContext(Context)
  return updateRound
}
export const useDeleteRound = () => {
  const { deleteRound } = useContext(Context)
  return deleteRound
}
export const useCopyRounds = () => {
  const { copyRounds } = useContext(Context)
  return copyRounds
}
export const useIsPermittedToConfigure = () => {
  const appCycle = useFocusedApplicationCycle()
  const currentAppCycle = useAppCycle()
  const isCurrent = appCycle === currentAppCycle

  const canConfigure = useCanManageRoundsAndRequirements()
  const isShadow = useIsShadow()
  const isRoundEditDisabled = useAppRoundEditDisabled()
  const canConfigureUpcoming = useConfigureUpcomingApplicationCycle()

  return canConfigure && (isShadow || (isCurrent && !isRoundEditDisabled) || (!isCurrent && canConfigureUpcoming))
}

export const useIsCurrentApplicationCycle = () => {
  const appCycle = useFocusedApplicationCycle()
  const currentAppCycle = useAppCycle()

  return appCycle === currentAppCycle
}

const AppRoundProvider = ({ applicationCycle, children }: { applicationCycle: number; children: React.ReactNode }) => {
  const [rounds, setRounds] = useState<AppRound[]>()
  const fetchRequirements = useFetchRequirements()

  const scid = useScid()
  const fetchRounds = useCallback(
    () =>
      api
        .get<RoundApi>(`/api/colleges/${scid}/application-deadlines/${applicationCycle}`)
        .catch(() => [])
        .then((payload) => {
          setRounds(
            (payload ?? []).map((round) => ({
              id: round.Id,
              updatedBy: round.UpdatedBy,
              updatedAt: round.UpdatedAt,
              updatedByName: round.UpdatedByName,
              updatedBySystem: round.UpdatedBySystem,
              scid: round.Scid,
              name: round.Name,
              customType: round.CustomType,
              binding: round.Binding,
              applicantType: round.ApplicantType,
              cutOff: round.RoundCutOff
                ? {
                    day: round.RoundCutOff?.Day,
                    month: round.RoundCutOff?.Month,
                  }
                : undefined,
              cutoffDate: round.CutoffDate,
              deadline: round.DueDate
                ? {
                    day: round.DueDate?.Day,
                    month: round.DueDate?.Month,
                  }
                : undefined,
              deadlineDate: round.DeadlineDate,
              materialsDeadlineDate: round.MaterialsDeadlineDate,
              requirementsId: round.RequirementsId,
            })),
          )
        }),
    [scid, applicationCycle],
  )

  const updateRound = useCallback(
    (round: AppRound) => {
      const apiFn = round.id ? api.put : api.post
      const isRolling = !round.cutoffDate
      return apiFn(`/api/colleges/${scid}/application-deadlines${round.id ? `/${round.id}` : ''}`, {
        Name: round.name,
        Binding: round.binding,
        ApplicantType: round.applicantType,
        CustomType: round.customType,
        CutoffDate: !round.cutoffDate || isRolling ? undefined : dateRelativeToUTC(round.cutoffDate),
        DeadlineDate: !round.deadlineDate || isRolling ? undefined : dateRelativeToUTC(round.deadlineDate),
        MaterialsDeadlineDate:
          !round.materialsDeadlineDate || isRolling ? undefined : dateRelativeToUTC(round.materialsDeadlineDate),
        RequirementsId: round.requirementsId,
        CollegeId: scid,
        ApplicationCycle: applicationCycle,
      }).then(fetchRounds)
    },
    [scid, fetchRounds, applicationCycle],
  )

  const deleteRound = useCallback(
    (roundId: string) =>
      api.delete<unknown>(`/api/colleges/${scid}/application-deadlines/${roundId}`).then(fetchRounds),
    [scid, fetchRounds],
  )

  const copyRounds = useCallback(
    (appCycle: number) => {
      return api
        .post(`/api/colleges/${scid}/application-deadlines/${appCycle}/copy`, {})
        .then(fetchRequirements)
        .then(fetchRounds)
    },
    [scid],
  )

  useEffect(() => {
    fetchRounds()
  }, [fetchRounds])

  const providerValue = useMemo(
    () => ({
      applicationCycle,
      rounds,
      updateRound,
      deleteRound,
      copyRounds,
    }),
    [applicationCycle, rounds, updateRound, deleteRound, copyRounds],
  )
  if (!rounds) {
    return null
  }
  return <Context.Provider value={providerValue}>{children}</Context.Provider>
}

export default AppRoundProvider
